import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router';

import debounce from 'lodash/debounce';

import { ViewMode } from '@/sections/Workspace/types';
import { useRecoilState } from 'recoil';

import { filterQueryState, filterSortByState, filterStatusState, viewModeState } from './atoms';
import { MatchParams, WorkspaceSections } from './types';

function useViewMode(section: WorkspaceSections, minimal: boolean = false) {
  const { teamId } = useParams<MatchParams>();
  const [viewMode, setViewMode] = useRecoilState(viewModeState(`${teamId}-${section}`));

  return { viewMode: minimal ? ViewMode.List : viewMode, setViewMode };
}

function useFilterQuery(section: WorkspaceSections) {
  const { teamId } = useParams<MatchParams>();
  const [filterQuery, setFilterQuery] = useRecoilState(filterQueryState(`${teamId}-${section}`));

  return { filterQuery, setFilterQuery };
}

function useFilterSortBy(section: WorkspaceSections) {
  const { teamId } = useParams<MatchParams>();
  const [filterSortBy, setFilterSortBy] = useRecoilState(filterSortByState(`${teamId}-${section}`));

  return { filterSortBy, setFilterSortBy };
}

function useFilterStatus(section: WorkspaceSections) {
  const { teamId } = useParams<MatchParams>();
  const [filterStatus, setFilterStatus] = useRecoilState(filterStatusState(`${teamId}-${section}`));

  return { filterStatus, setFilterStatus };
}

function useForceUpdateOnWindowResize(debounceMs: number = 100) {
  const [forceUpdateKey, setForceUpdateKey] = useState(0);

  const debouncedResize = useMemo(
    () => debounce(() => setForceUpdateKey((prev) => prev + 1), debounceMs),
    [debounceMs],
  );

  useEffect(() => {
    window.addEventListener('resize', debouncedResize);

    return () => {
      window.removeEventListener('resize', debouncedResize);
      debouncedResize.cancel();
    };
  }, [debouncedResize]);

  return forceUpdateKey;
}

function useTableContainerHeight(headerRef: React.RefObject<HTMLDivElement>, key: number): number {
  const [height, setHeight] = useState(0);
  const forceUpdateKey = useForceUpdateOnWindowResize();

  const measureHeight = useCallback((element: HTMLDivElement | null) => {
    if (element) {
      setHeight(element.clientHeight);
    }
  }, []);

  useLayoutEffect(() => {
    if (headerRef.current) {
      measureHeight(headerRef.current);
    }
  }, [measureHeight, headerRef, key, forceUpdateKey]);

  return height;
}

export { useViewMode, useFilterQuery, useFilterSortBy, useFilterStatus, useTableContainerHeight };
